import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as server from "../dienst/server";

/*
  Wer ist angemeldet?

  Beim Start wird geprüft, ob die gemerkte Anmeldung noch gilt. Solange das
  läuft, zeigt die App eine Wartezeile — sonst blitzt die Anmeldemaske kurz
  auf, obwohl man längst angemeldet ist.
*/

const Zusammenhang = createContext(null);

export function AnmeldungProvider({ children }) {
  const gemerkt = server.gemerkteAnmeldung();

  const [token, setzeToken] = useState(gemerkt ? gemerkt.token : null);
  const [konto, setzeKonto] = useState(gemerkt ? gemerkt.konto : null);
  const [fraktion, setzeFraktion] = useState(null);
  const [wirdGeprueft, setzeWirdGeprueft] = useState(Boolean(gemerkt));

  /* Gilt die gemerkte Anmeldung noch? */
  useEffect(() => {
    if (!gemerkt) return;
    let abgebrochen = false;

    server
      .frischeAnmeldung(gemerkt.token)
      .then((daten) => {
        if (abgebrochen) return;
        setzeToken(daten.token);
        setzeKonto(daten.record);
      })
      .catch((fehler) => {
        if (abgebrochen) return;
        /*
          Kein Netz heißt nicht abgemeldet. Nur wenn der Server ausdrücklich
          sagt „gilt nicht mehr", fliegt die Anmeldung raus — sonst wäre man
          bei jedem Funkloch draußen.
        */
        if (fehler.status === 401 || fehler.status === 403) {
          server.vergissAnmeldung();
          setzeToken(null);
          setzeKonto(null);
        }
      })
      .finally(() => {
        if (!abgebrochen) setzeWirdGeprueft(false);
      });

    return () => {
      abgebrochen = true;
    };
    // Absichtlich nur beim Start.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Die Fraktion dazuladen, damit ihr Name in der Kopfzeile stehen kann. */
  useEffect(() => {
    if (!token || !konto || !konto.fraktion) {
      setzeFraktion(null);
      return;
    }
    let abgebrochen = false;
    server.holeFraktion(token, konto.fraktion).then((f) => {
      if (!abgebrochen) setzeFraktion(f);
    });
    return () => {
      abgebrochen = true;
    };
  }, [token, konto]);

  const uebernimm = useCallback((daten) => {
    setzeToken(daten.token);
    setzeKonto(daten.record);
  }, []);

  const aktionen = useMemo(
    () => ({
      async anmelden(email, passwort) {
        uebernimm(await server.melde(email, passwort));
      },

      async registrieren(angaben) {
        uebernimm(await server.registriere(angaben));
      },

      async passwortNeu(code, passwort) {
        uebernimm(await server.setzePasswortNeu(code, passwort));
      },

      abmelden() {
        server.vergissAnmeldung();
        setzeToken(null);
        setzeKonto(null);
        setzeFraktion(null);
      },

      async profilSpeichern(angaben) {
        const neu = await server.speichereProfil(token, konto.id, angaben);
        setzeKonto(neu);
        return neu;
      },
    }),
    [token, konto, uebernimm]
  );

  const wert = useMemo(
    () => ({
      token,
      konto,
      fraktion,
      angemeldet: Boolean(token && konto),
      wirdGeprueft,
      aktionen,
      bildAdresse: konto ? server.dateiAdresse(konto, "avatar") : null,
    }),
    [token, konto, fraktion, wirdGeprueft, aktionen]
  );

  return <Zusammenhang.Provider value={wert}>{children}</Zusammenhang.Provider>;
}

export function useAnmeldung() {
  const wert = useContext(Zusammenhang);
  if (!wert) throw new Error("useAnmeldung muss innerhalb von AnmeldungProvider stehen.");
  return wert;
}
